import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import {
    formatDataGeneral,
    getAttributeValue,
    getFileLinkIfExist,
    getFirstFileLinkIfExist,
} from "utils/utils";
import {
    renderDpatEvidenceLink,
    scoreOnlyIfDocumentLinksComplete,
} from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const PROJECT_PROGRAM = "g3wMUKEMmH3";

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function getYearFromAttributeValue(value) {
    if (value == null || value === "" || value === "N/A") return null;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.getFullYear();
    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
}

function isProjectConcernedInSelectedYear(item, selectedYear) {
    const year = Number(selectedYear);
    if (!Number.isFinite(year)) return false;

    // Use actual dates, not expected ones.
    const startYear = getYearFromAttributeValue(
        getAttributeValue("Start Date", item),
    );
    const endYear = getYearFromAttributeValue(
        getAttributeValue("End Date", item),
    );

    // First check startDate belongs to selectedYear, otherwise check endDate.
    const years = isDpatScoreRelaxationExempt() ? [year - 1, year] : [year];
    return years.some((activeYear) =>
        (startYear != null && startYear === activeYear)
        || (endYear != null && endYear === activeYear),
    );
}

function buildAuditorGeneralReportLinks(documents) {
    const documentData = documents?.data;
    const documentReports = documents?.reports;
    const auditorGeneralDocs = formatDataGeneral(
        documentData,
        "DPAT | M&E Tool Type",
        "Auditor-General Reports",
    ) || [];

    let auditorGeneralReportLink = "";
    if (auditorGeneralDocs.length > 0) {
        auditorGeneralReportLink = getFileLinkIfExist(
            documentReports,
            "MjPmcbueEdU",
            auditorGeneralDocs[0]?.trackedEntity,
        );
    }

    return [
        {
            title: "Auditor-General Reports",
            link: auditorGeneralReportLink
                ? fileUrl(auditorGeneralReportLink, "MjPmcbueEdU")
                : "Not Uploaded",
        },
    ];
}

function buildProjectRows(instances, reports, selectedYear) {
    const projects = (formatDataGeneral(instances, "Project & Programme Type", "Project") || [])
        .filter((item) => isProjectConcernedInSelectedYear(item, selectedYear));
    const temp = [];

    projects.forEach((item, index) => {
        const reportLink = getFirstFileLinkIfExist(reports, "i1171kI9OBD", item.trackedEntity, "TrdgYOz3XUL");
        const contractLink = getFirstFileLinkIfExist(reports, "TKC9UdFpqB6", item.trackedEntity, "rRWa5ghYO35");
        const adverseFinding = getFirstFileLinkIfExist(
            reports,
            "YDgvR2PKQTT",
            item.trackedEntity,
            "rRWa5ghYO35",
            "status",
        );

        const startDate = getAttributeValue("Start Date", item);
        const endDate = getAttributeValue("End Date", item);

        temp.push({
            key: item.trackedEntity || index + 1,
            no: index + 1,
            project: getAttributeValue("Name", item),
            startDate: startDate !== "N/A" ? startDate : "Not Provided",
            endDate: endDate !== "N/A" ? endDate : "Not Provided",
            status: adverseFinding
                ? adverseFinding === "true"
                    ? "YES"
                    : "NO"
                : "Not Provided",
            report: reportLink ? fileUrl(reportLink, "i1171kI9OBD") : "Not Uploaded",
            contract: contractLink ? fileUrl(contractLink, "TKC9UdFpqB6") : "Not Uploaded",
        });
    });

    let score1 = 1;
    let score2 = 1;
    let score3 = 1;

    for (const project of temp) {
        if (project.contract === "Not Uploaded") {
            score1 = 0;
        }
        if (project.report === "Not Uploaded") {
            score2 = 0;
        }
        if (project.status === "YES") {
            score3 = 0;
        }
    }

    if (temp.length === 0) {
        score1 = 0;
        score2 = 0;
        score3 = 0;
    }

    return { projectList: temp, scorei: score1, scoreii: score2, scoreiii: score3 };
}

const FollowUpDeduction = forwardRef(({
  year,
  district,
  data,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {

    const { Title } = Typography;
    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const districtId =
        typeof district === "string"
            ? district
            : district?.value?.id ?? district?.id ?? "";
    const [projectList, setProjectList] = useState([]);
    const [documentLinks, setDocumentLinks] = useState([]);
    const [scorei, setScorei] = useState(0);
    const [scoreii, setScoreii] = useState(0);
    const [scoreiii, setScoreiii] = useState(0);
    const [maxScore, setMaxScore] = useState(3);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI1",
            area: "Annual Action Plan Implementation",
            maxScore,
            projectList,
            links: documentLinks,
            scorei,
            scoreii,
            scoreiii,
        }),
    }));

    const DPAT_SLUG = "pi1.0-1.4";

    const projectColumns = [
        { title: "No", dataIndex: "no", key: "no" },
        { title: "Projects", dataIndex: "project", key: "project" },
        { title: "Start Date", dataIndex: "startDate", key: "startDate" },
        { title: "End Date", dataIndex: "endDate", key: "endDate" },
        {
            title: "Is there any Adverse Findings on Central Government Deductions?",
            dataIndex: "status",
            key: "status",
        },
        {
            title: "Report",
            dataIndex: "report",
            key: "report",
            render: (value) => renderDpatEvidenceLink(value, "View Report"),
        },
        {
            title: "Contract",
            dataIndex: "contract",
            key: "contract",
            render: (value) => renderDpatEvidenceLink(value, "View Contract"),
        },
    ];

    const documentTableColumns = useMemo(
        () => documentsColumn.map((col) =>
            col.dataIndex === "link"
                ? {
                    ...col,
                    render: (value) => renderDpatEvidenceLink(value, "View Document"),
                }
                : col
        ),
        [],
    );

    const loadFromDhis2 = useCallback(async () => {
        if (!districtId || resolvedYear == null) return;

        try {
            let instances = Array.isArray(data?.data) ? data.data : [];
            let reports = Array.isArray(data?.reports) ? data.reports : [];

            if (!instances.length) {
                const entitiesResp = await axios.get("/tracker/trackedEntities", {
                    params: {
                        orgUnit: districtId,
                        program: PROJECT_PROGRAM,
                        pageSize: 5000,
                    },
                });

                instances = entitiesResp?.data?.instances || [];

                if (instances.length) {
                    const eventsResp = await axios.get("/tracker/events", {
                        params: {
                            program: PROJECT_PROGRAM,
                            orgUnit: districtId,
                            pageSize: 5000,
                        },
                    });
                    reports = eventsResp?.data?.instances || [];
                }
            }

            const {
                projectList: rows,
                scorei: s1,
                scoreii: s2,
                scoreiii: s3,
            } = buildProjectRows(instances, reports, resolvedYear);

            const docLinks = buildAuditorGeneralReportLinks(documents);
            setProjectList(rows);
            setDocumentLinks(docLinks);
            setScorei(scoreOnlyIfDocumentLinksComplete(docLinks, s1));
            setScoreii(scoreOnlyIfDocumentLinksComplete(docLinks, s2));
            setScoreiii(scoreOnlyIfDocumentLinksComplete(docLinks, s3));
        } catch (err) {
            console.error("FollowUpDeduction fetch failed:", err);
            setProjectList([]);
            setDocumentLinks([]);
            setScorei(0);
            setScoreii(0);
            setScoreiii(0);
        }
    }, [districtId, resolvedYear, data, documents]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            setProjectList(snap?.projectList ?? []);
            setDocumentLinks(snap?.links ?? []);
            setScorei(snap?.scorei ?? 0);
            setScoreii(snap?.scoreii ?? 0);
            setScoreiii(snap?.scoreiii ?? 0);
            setMaxScore(snap?.maxScore ?? 3);
        },
        deps: [year, district, data, documents, loadFromDhis2],
    });

    return (
        <Comment
            data={projectList}
            year={resolvedYear}
            districtId={districtId}
            tableCommentedId={`pi1.0-1.4-${resolvedYear}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={resolvedYear}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                              <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.4i Actual Score:" score={scorei} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.4ii Actual Score:" score={scoreii} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.4iii Actual Score:" score={scoreiii} />
                    </IndicatorScoreSection>
                    <Title level={4} style={{ marginTop: "20px" }}>
                        Evidence of contract deductions at source
                    </Title>

                    <Table
                        columns={projectColumns}
                        dataSource={projectList}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />

                    <Title level={5} style={{ marginTop: "20px" }}>
                        Evidence of Attached Documents
                    </Title>
                    <Table
                        columns={documentTableColumns}
                        dataSource={documentLinks}
                        pagination={false}
                        bordered
                        rowKey="title"
                    />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
});

export default FollowUpDeduction;
